import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import Lenis from "lenis";
import TopBar from "@/components/TopBar";
import Progress from "@/components/Progress";
import Hero from "@/components/Hero";
import NumberStep from "@/components/NumberStep";
import ChoiceStep from "@/components/ChoiceStep";
import IndustryStep from "@/components/IndustryStep";
import Result from "@/components/Result";
import Adritools from "@/components/Adritools";
import { AUTORIDAD, PRODUCCION, RELEVANCIA } from "@/lib/calc";
import { initPixel } from "@/lib/pixel";

const INITIAL = {
  seguidores: "",
  interacciones: "",
  autoridad: 1.0,
  autoridadId: "establecido",
  produccion: 1.35,
  produccionId: "alto",
  relevancia: "perfecto",
  industria: "travel",
  cpl: 4,
};

const stepMotion = {
  initial: { opacity: 0, x: 44 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.38, ease: [0.22, 0.61, 0.36, 1] } },
  exit: { opacity: 0, x: -44, transition: { duration: 0.24, ease: [0.22, 0.61, 0.36, 1] } },
};

function Calculator({ lenisRef }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);

  useEffect(() => {
    document.title = "Calculadora de Tarifas · Creatividad²";
  }, []);

  function go(n) {
    setStep(n);
    requestAnimationFrame(() => {
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    });
  }

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));

  const seg = Number(data.seguidores);
  const inter = Number(data.interacciones);
  const engagementPreview =
    seg > 0 && data.interacciones !== "" && !Number.isNaN(inter) ? (
      <p className="eng-prev" data-testid="engagement-preview">
        Engagement: <b>{((inter / seg) * 100).toFixed(1)}%</b>
      </p>
    ) : null;

  const screens = [
    <Hero key="hero" onStart={() => go(1)} />,
    <NumberStep
      key="s1"
      chapter={1}
      title="¿Cuántos seguidores tienes?"
      hint="Tu número total en la plataforma donde vas a postear."
      value={data.seguidores}
      onChange={(v) => upd({ seguidores: v })}
      onSubmit={() => go(2)}
      onBack={() => go(0)}
      placeholder="Ej. 60000"
      testId="step-seguidores"
    />,
    <NumberStep
      key="s2"
      chapter={2}
      title="Tus interacciones promedio por post"
      hint="Likes + comentarios + saves + shares. Con esto calculo tu engagement, sin que te autopuntués."
      value={data.interacciones}
      onChange={(v) => upd({ interacciones: v })}
      onSubmit={() => go(3)}
      onBack={() => go(1)}
      placeholder="Ej. 3000"
      min={0}
      testId="step-interacciones"
      preview={engagementPreview}
    />,
    <ChoiceStep
      key="s3"
      chapter={3}
      title="¿Cómo es tu autoridad?"
      options={AUTORIDAD}
      selected={data.autoridadId}
      onPick={(o) => upd({ autoridadId: o.id, autoridad: o.mult })}
      onNext={() => go(4)}
      onBack={() => go(2)}
      testId="step-autoridad"
    />,
    <ChoiceStep
      key="s4"
      chapter={4}
      title="Nivel de producción"
      options={PRODUCCION}
      selected={data.produccionId}
      onPick={(o) => upd({ produccionId: o.id, produccion: o.mult })}
      onNext={() => go(5)}
      onBack={() => go(3)}
      testId="step-produccion"
    />,
    <ChoiceStep
      key="s5"
      chapter={5}
      title="Relevancia: tu nicho vs el de la marca"
      options={RELEVANCIA}
      selected={data.relevancia}
      onPick={(o) => upd({ relevancia: o.id })}
      onNext={() => go(6)}
      onBack={() => go(4)}
      testId="step-relevancia"
    />,
    <IndustryStep
      key="s6"
      selected={data.industria}
      onSelect={(ind) => upd({ industria: ind.id, ...(ind.cpl !== null ? { cpl: ind.cpl } : {}) })}
      onAdvance={(cplManual) => {
        if (cplManual) upd({ cpl: cplManual });
        go(7);
      }}
      onBack={() => go(5)}
      testId="step-industria"
    />,
    <Result
      key="res"
      data={data}
      onBack={() => go(6)}
      onRestart={() => {
        setData(INITIAL);
        go(0);
      }}
    />,
  ];

  return (
    <div>
      <TopBar />
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={stepMotion.initial} animate={stepMotion.animate} exit={stepMotion.exit}>
          {screens[step]}
        </motion.div>
      </AnimatePresence>
      <Progress step={step} />
    </div>
  );
}

export default function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    initPixel();
    document.fonts?.ready.then(() => document.documentElement.classList.add("fonts-ready"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.05 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Adritools />} />
            <Route path="/calcula-tu-rate" element={<Calculator lenisRef={lenisRef} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </MotionConfig>
  );
}
